import { useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import {
  Users,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  PlayCircle,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { usePeopleImport, type ImportReport } from '../../hooks/usePeopleImport';
import { Button } from '../../components/ui/Button';
import {
  AllocationTargetField,
  type AllocationTarget,
} from '../../components/admin/AllocationTargetField';
import { ApiError } from '../../lib/api';

// Matches the importer's header aliases. Start dates may be UK
// DD/MM/YYYY or ISO; Position must match an existing job role name.
const TEMPLATE_CSV = `Firstname,Surname,Email,Personal Email,Employee ID,Position,Manager,Start Date,Contract,Notes,Existing Staff
Jamie,Reed,jamie.reed@corrigenda.co.uk,jamie.reed@gmail.com,E1234,M&E Engineer,Sam Carter,01/08/2026,HCC,Prefers Jamie,No
Alex,Wong,alex.wong@corrigenda.co.uk,,E1235,Plumber,Sam Carter,01/08/2026,HCC,,No
Pat,Quinn,pat.quinn@corrigenda.co.uk,,E1240,Electrician,Sam Carter,,HCC,Existing employee collecting new kit,Yes
`;

/**
 * Bulk-import attendees from a CSV file (CLAUDE.md 6.1).
 *
 * Flow: pick a file -> read it client-side via FileReader -> POST as
 * a JSON string. Dry-run is on by default so the first click never
 * persists; the operator must explicitly toggle it off after reviewing.
 */
export function ImportAttendees() {
  const { data: user, isLoading } = useCurrentUser();
  const importMutation = usePeopleImport();
  const [csv, setCsv] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [filesize, setFilesize] = useState<number | null>(null);
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [target, setTarget] = useState<AllocationTarget>(undefined);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You need to <a href="/login" className="underline">sign in</a> first.
      </main>
    );
  }
  const allowed = user.roles.includes('admin') || user.roles.includes('coordinator');
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to import attendees.
      </main>
    );
  }

  function downloadTemplate() {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'attendee-import-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setError(null);
    setReport(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setFilesize(file.size);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.onerror = () => setError('failed to read file');
    reader.readAsText(file);
  }

  // Scoped sessions inherit the header switcher's mobilisation; the
  // all-mobilisations view must pick a destination explicitly.
  const scoped = !!user.current_mobilisation_id;
  const targetChosen = scoped || target !== undefined;

  async function onSubmit() {
    if (!csv || !targetChosen) return;
    setError(null);
    try {
      const r = await importMutation.mutateAsync({
        csv,
        dry_run: dryRun,
        ...(scoped ? {} : { mobilisation_id: target ?? null }),
      });
      setReport(r);
      if (!dryRun) {
        toast.success(
          `Imported ${r.inserted} new, ${r.updated} updated`,
        );
      }
    } catch (err) {
      setReport(null);
      const msg = err instanceof ApiError ? err.message : 'import failed';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Users className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Import attendees
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Upload a CSV exported from HR. Required columns:
              <code className="mx-1 rounded bg-surface-2 px-1 text-xs">Firstname</code>,
              <code className="mx-1 rounded bg-surface-2 px-1 text-xs">Surname</code>,
              <code className="mx-1 rounded bg-surface-2 px-1 text-xs">Email</code>.
              Optional: Personal Email, Employee ID, Position, Manager,
              Start Date (DD/MM/YYYY), Contract, Notes, Existing Staff
              (Yes/No).
            </p>
            <p className="mt-1 text-xs text-fg-muted">
              Set <code className="rounded bg-surface-2 px-1">Existing Staff</code> to
              Yes for current employees who only need to sign in and collect
              kit. They skip onboarding but, given a Position, still get a kit
              checklist. Everyone imported is added to the mobilisation chosen
              below.
            </p>
            <button
              type="button"
              onClick={downloadTemplate}
              className="mt-2 text-xs font-medium text-fg-muted underline-offset-2 hover:underline"
            >
              Download template CSV
            </button>
          </div>
        </header>

        <section className="mt-6 rounded-lg border border-line/10 bg-surface p-5 shadow-sm sm:p-6">
          <label
            htmlFor="csv-file"
            className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
              csv
                ? 'border-success/25 bg-success-soft/15'
                : 'border-line-strong/20 bg-surface-2 hover:bg-surface-2'
            }`}
          >
            {csv ? (
              <>
                <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
                <div>
                  <div className="text-sm font-semibold text-success-soft-fg">
                    {filename}
                  </div>
                  <div className="text-xs text-success-soft-fg">
                    {filesize ? `${(filesize / 1024).toFixed(1)} KB · ` : ''}Click to choose a different file
                  </div>
                </div>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-fg-dim" />
                <div>
                  <div className="text-sm font-semibold text-fg-muted">
                    Click to choose a CSV file
                  </div>
                  <div className="text-xs text-fg-muted">
                    or drop one here
                  </div>
                </div>
              </>
            )}
            <input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="sr-only"
            />
          </label>

          <div className="mt-4">
            <AllocationTargetField value={target} onChange={setTarget} />
          </div>

          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-md border border-line/10 p-3 transition-colors hover:bg-surface-2">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-line-strong/20"
            />
            <div className="text-sm">
              <div className="font-medium">Dry-run (validate only)</div>
              <div className="text-xs text-fg-muted">
                Read the CSV and show what would happen without writing anything to
                the database. Untick to actually import.
              </div>
            </div>
          </label>

          {error ? (
            <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              size="md"
              iconLeft={<PlayCircle className="h-4 w-4" />}
              disabled={!csv || !targetChosen}
              loading={importMutation.isPending}
              onClick={onSubmit}
            >
              {dryRun ? 'Run dry-run' : 'Apply import'}
            </Button>
          </div>
        </section>

        {report ? (
          <section className="mt-6 rounded-lg border border-line/10 bg-surface p-5 shadow-sm sm:p-6">
            <div className="flex items-center gap-2">
              <CheckCircle2
                className={`h-5 w-5 ${
                  report.errors.length === 0 ? 'text-emerald-600' : 'text-amber-600'
                }`}
              />
              <h2 className="text-lg font-semibold">
                {report.dry_run ? 'Dry-run report' : 'Import report'}
              </h2>
            </div>
            <dl className="mt-4 grid grid-cols-3 gap-4">
              <Stat label="Total rows" value={report.total} tone="slate" />
              <Stat
                label={report.dry_run ? 'Would insert' : 'Inserted'}
                value={report.inserted}
                tone="emerald"
              />
              <Stat
                label={report.dry_run ? 'Would update' : 'Updated'}
                value={report.updated}
                tone="indigo"
              />
            </dl>
            {report.errors.length > 0 ? (
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-danger-soft-fg">
                  {report.errors.length} error{report.errors.length === 1 ? '' : 's'}
                </h3>
                <div className="mt-2 overflow-hidden rounded-md border border-danger/25">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-danger-soft/15 text-xs uppercase tracking-wider text-danger-soft-fg">
                      <tr>
                        <th className="px-3 py-2">Row</th>
                        <th className="px-3 py-2">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.errors.map((e) => (
                        <tr key={e.row} className="border-t border-danger/25">
                          <td className="px-3 py-2 font-mono text-xs">{e.row}</td>
                          <td className="px-3 py-2 text-xs">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="mt-3 inline-flex items-center gap-1 rounded-md bg-success-soft/15 px-2 py-1 text-xs font-medium text-success-soft-fg">
                <CheckCircle2 className="h-3 w-3" />
                No errors
              </p>
            )}
          </section>
        ) : null}
      </div>
    </main>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'slate' | 'emerald' | 'indigo';
}) {
  const tones = {
    slate: 'bg-surface-2 text-fg-muted',
    emerald: 'bg-success-soft/15 text-success-soft-fg',
    indigo: 'bg-accent/10 text-accent',
  } as const;
  return (
    <div className={`rounded-lg p-3 ${tones[tone]}`}>
      <dt className="text-xs font-medium uppercase tracking-wider opacity-80">
        {label}
      </dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
